// CDN Image Management
// This file centralizes all image paths for easy maintenance
// All images are served from Aliyun CDN for production performance

// CDN Configuration
const CDN_BASE_URL = 'https://us-silicon-cdn001.oss-us-west-1.aliyuncs.com/musheng-clothing';
const USE_CDN = import.meta.env.PROD || import.meta.env.VITE_USE_CDN === 'true';

// Helper function to get CDN or local image URL
export const getImageUrl = (localPath: string): string => {
  if (USE_CDN) {
    // Remove leading slash and prepend CDN URL
    const cleanPath = localPath.startsWith('/') ? localPath.slice(1) : localPath;
    // Only encode the filename portion, not the directory structure
    const lastSlashIndex = cleanPath.lastIndexOf('/');
    const directory = cleanPath.substring(0, lastSlashIndex + 1);
    const filename = cleanPath.substring(lastSlashIndex + 1);
    const encodedFilename = encodeURIComponent(filename);
    return `${CDN_BASE_URL}/${directory}${encodedFilename}`;
  }
  // Return local path for development
  return localPath;
};

// Hero Section Images
export const heroImages = {
  main: getImageUrl('/images/hero/hero-main.jpg'),
  manufacturing: getImageUrl('/images/hero/hero-manufacturing.jpg'),
  placeholder: getImageUrl('/images/placeholders/hero-placeholder.jpg')
};

// Individual Page Hero Images
export const pageHeroImages = {
  services: getImageUrl('/images/hero/hero-services.jpg'),
  sustainability: getImageUrl('/images/hero/hero-sustainability.jpg'),
  sustainabilityPractices: getImageUrl('/images/hero/hero-sustainability-practices.jpg'),
  sustainabilityPromise: getImageUrl('/images/hero/hero-sustainability-promise.jpg')
};

// About Section Images
export const aboutImages = {
  manufacturing: getImageUrl('/images/about/about-manufacturing.jpg'),
  craftsman: getImageUrl('/images/about/about-craftsman.jpg'),
  facility: getImageUrl('/images/about/about-facility.jpg'),
  hero: getImageUrl('/images/about/about-hero.jpg'),
  capabilities: {
    leatherDevelopment: getImageUrl('/images/about/capabilities-leather-development.jpg'),
    materialInnovation: getImageUrl('/images/about/capabilities-material-innovation.jpg'),
    flexibleProduction: getImageUrl('/images/about/capabilities-flexible-production.jpg'),
    globalSupport: getImageUrl('/images/about/capabilities-global-support.jpg')
  },
  placeholder: getImageUrl('/images/placeholders/about-placeholder.jpg')
};

// Services Images
export const servicesImages = {
  oemOdm: getImageUrl('/images/services/service-oem-odm.jpg'),
  oemOdmWebp: getImageUrl('/images/services/service-oem-odm.webp'),
  leather: getImageUrl('/images/services/service-leather.jpg'),
  leatherWebp: getImageUrl('/images/services/service-leather.webp'),
  outerwear: getImageUrl('/images/services/service-outerwear.jpg'),
  outerwearWebp: getImageUrl('/images/services/service-outerwear.webp'),
  production: getImageUrl('/images/services/service-production.jpg'),
  productionWebp: getImageUrl('/images/services/service-production.webp'),
  trade: getImageUrl('/images/services/service-trade.jpg'),
  tradeWebp: getImageUrl('/images/services/service-trade.webp'),
  placeholder: getImageUrl('/images/placeholders/service-placeholder.jpg')
};

// Factory Images
export const factoryImages = {
  huaxin: getImageUrl('/images/factories/factory-huaxin.jpg'),
  dongming: getImageUrl('/images/factories/factory-dongming.jpg'),
  placeholder: getImageUrl('/images/placeholders/factory-placeholder.jpg')
};

// Quality Assurance Images
export const qualityImages = {
  design: getImageUrl('/images/quality/quality-design.jpg'),
  production: getImageUrl('/images/quality/quality-production.jpg'),
  control: getImageUrl('/images/quality/quality-control.jpg'),
  shipping: getImageUrl('/images/quality/quality-shipping.jpg'),
  placeholder: getImageUrl('/images/placeholders/quality-placeholder.svg')
};

// Craftsmanship Images
export const craftsmanshipImages = {
  screenPrint: getImageUrl('/images/products/craftmenship/screen-print.jpg'),
  handMadeRub: getImageUrl('/images/products/craftmenship/hand-made-rub.jpg'),
  heatPress: getImageUrl('/images/products/craftmenship/heat-press.jpg'),
  handStitch: getImageUrl('/images/products/craftmenship/hand-stitch.jpg'),
  garmentDye: getImageUrl('/images/products/craftmenship/Garment_dye.jpg'),
  doubleSideLeather: getImageUrl('/images/products/craftmenship/double-side-leather.jpg'),
  seamLess: getImageUrl('/images/products/craftmenship/seam-less.jpg'),
  embroideryPattern: getImageUrl('/images/products/craftmenship/embroidery-pattern.jpg'),
  embroideryStitch: getImageUrl('/images/products/craftmenship/embroidery-stitch.jpg'),
  bonding: getImageUrl('/images/products/craftmenship/bonding.JPG'),
  digitalPrint: getImageUrl('/images/products/craftmenship/digital_print.jpg'),
  leatherGarmentWashing: getImageUrl('/images/products/craftmenship/leather-garment-washing.jpg')
};

// Certification Images
export const certificationImages = {
  bsci: getImageUrl('/images/certifications/bsci-certification.png'),
  sedex: getImageUrl('/images/certifications/sedex-certification.png'),
  lwg: getImageUrl('/images/certifications/lwg-certification.png'),
  rds: getImageUrl('/images/certifications/rds-certification.png'),
  grs: getImageUrl('/images/certifications/grs-certification.png'),
  iso9001: getImageUrl('/images/certifications/iso-9001-certification.png'),
  iso14001: getImageUrl('/images/certifications/iso-14001-certification.png')
};

// Sustainability Images
export const sustainabilityImages = {
  ethicalProduction: getImageUrl('/images/sustainability/ethical-production.jpg'),
  placeholder: getImageUrl('/images/placeholders/sustainability-placeholder.jpg')
};

// Contact Images
export const contactImages = {
  hk: getImageUrl('/images/contact/contact-hk.jpg'),
  ny: getImageUrl('/images/contact/contact-ny.jpg'),
  china: getImageUrl('/images/contact/contact-china.jpg'),
  placeholder: getImageUrl('/images/placeholders/contact-placeholder.jpg')
};

// Logo Images
export const logoImages = {
  main: getImageUrl('/images/logos/logo-main.png'),
  white: getImageUrl('/images/logos/logo-white.png'),
  icon: getImageUrl('/images/logos/logo-main.png')
};

// Brand Logos for Trust Section
export const brandLogos = [
  getImageUrl('/images/LOGO/2024050315-b5c19c52-a857-4723-8b92-3e1e1f89b31e-pajar_sport_logo_black_no-registered.png'),
  getImageUrl('/images/LOGO/229-2295259_roy-rogers-is-an-italian-brand-born-in.png'),
  getImageUrl('/images/LOGO/7-for-All-Mankind-logo.png'),
  getImageUrl('/images/LOGO/BARACUTA_logo_500px.png'),
  getImageUrl('/images/LOGO/Hugo-Boss-Logo.png'),
  getImageUrl('/images/LOGO/Liu_Jo_logo.svg.png'),
  getImageUrl('/images/LOGO/oakfort.png'),
  getImageUrl('/images/LOGO/Mackage__Logo.png'),
  getImageUrl('/images/LOGO/Moose-Knuckles-logo.png'),
  getImageUrl('/images/LOGO/O1CN01N940SZ1PLRKOArGm5_!!1667651824.png'),
  getImageUrl('/images/LOGO/TIPA_Fashion-logos_scotchsoda.png'),
  getImageUrl('/images/LOGO/drome-logo-black.png'),
  getImageUrl('/images/LOGO/logo-purple-brand.png'),
  getImageUrl('/images/LOGO/rudsak_logo_before_after.png')
];

// Placeholder Images - Use these as fallbacks
export const placeholderImages = {
  hero: getImageUrl('/images/placeholders/placeholder-hero.jpg'),
  service: getImageUrl('/images/placeholders/placeholder-service.jpg'),
  about: getImageUrl('/images/placeholders/placeholder-about.jpg'),
  contact: getImageUrl('/images/placeholders/placeholder-contact.jpg'),
  quality: getImageUrl('/images/placeholders/placeholder-quality.jpg'),
  general: getImageUrl('/images/placeholders/placeholder-general.jpg')
};

// Image Configuration
export const imageConfig = {
  // Default image dimensions
  hero: { width: 1920, height: 1080 },
  service: { width: 600, height: 400 },
  about: { width: 600, height: 800 },
  contact: { width: 600, height: 400 },
  qualityImg: { width: 600, height: 400 },

  // Image optimization settings
  quality: 85,
  format: 'webp',
  fallbackFormat: 'jpg'
};

// Helper function to get image with fallback
export const getImage = (imagePath: string, fallbackPath: string = placeholderImages.general) => {
  // In development, return the path
  // In production, you might want to add error handling
  return imagePath;
};

// Helper function to get responsive image
export const getResponsiveImage = (imagePath: string, size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizeSuffix = size === 'sm' ? '-sm' : size === 'lg' ? '-lg' : '';
  const basePath = imagePath.replace(/\.(jpg|jpeg|png|webp)$/, '');
  return `${basePath}${sizeSuffix}.${imageConfig.format}`;
};

// Image loading states
export const imageStates = {
  loading: getImageUrl('/images/placeholders/loading.svg'),
  error: getImageUrl('/images/placeholders/error.svg')
};

// Product Category Images
export const productCategoryImages = {
  leatherJackets: getImageUrl('/images/products/product-leather-jackets.jpg'),
  leatherJacketsWebp: getImageUrl('/images/products/product-leather-jackets.webp'),
  shearling: getImageUrl('/images/products/product-shearling.jpg'),
  shearlingWebp: getImageUrl('/images/products/product-shearling.webp'),
  puFauxFur: getImageUrl('/images/products/product-pu-faux-fur.jpg'),
  puFauxFurWebp: getImageUrl('/images/products/product-pu-faux-fur.webp'),
  downJackets: getImageUrl('/images/products/product-down-jackets.jpg'),
  downJacketsWebp: getImageUrl('/images/products/product-down-jackets.webp'),
  placeholder: getImageUrl('/images/placeholders/product-placeholder.jpg')
};

// Individual Product Images for Categories
export const productGalleryImages = {
  leather: [
    getImageUrl('/images/products/LEATHER/018c25391a57dffcdfd49071b5d2d362.JPG'),
    getImageUrl('/images/products/LEATHER/37d2ac6220e14f039a9f387795a1a31e.JPG'),
    getImageUrl('/images/products/LEATHER/6c4c10070b346f397e61b03c3d3c3874.JPG'),
    getImageUrl('/images/products/LEATHER/6f0dce1bfee9d0c08709033de29d6d74.JPG'),
    getImageUrl('/images/products/LEATHER/73b5e177ac69ae29c94fb55fcd46fbec.JPG'),
    getImageUrl('/images/products/LEATHER/77bc0aae94579c893623294523d159fb.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_5530.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6326 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6327 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6329 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6816 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6817 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6818 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6820 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6821 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6822 2.JPG'),
    getImageUrl('/images/products/LEATHER/IMG_6823 2.JPG')
  ],
  shearling: [
    getImageUrl('/images/products/SHEARLING/043dc4366210ace40e0769aebc1e1408.JPG'),
    getImageUrl('/images/products/SHEARLING/2e9f1418b41a8b214da1a06dd7858eb5.JPG'),
    getImageUrl('/images/products/SHEARLING/7c344c07e28964b0a998798bda6c72c5.JPG'),
    getImageUrl('/images/products/SHEARLING/957be7d7defe73abd1267f087572a2f5.JPG'),
    getImageUrl('/images/products/SHEARLING/IMG_6331 2.JPG'),
    getImageUrl('/images/products/SHEARLING/IMG_6819 2.JPG')
  ],
  puFauxFur: [
    getImageUrl('/images/products/FAUX_FUR/2024-1-29羽绒服.png'),
    getImageUrl('/images/products/FAUX_FUR/2024-1-29羽绒服7.png'),
    getImageUrl('/images/products/FAUX_FUR/2024-1-29羽绒服8322.png')
  ],
  downJackets: [
    getImageUrl('/images/products/PUFFER/2024-1-28288.jpg'),
    getImageUrl('/images/products/PUFFER/2024-1-29.jpg'),
    getImageUrl('/images/products/PUFFER/2024-1-298227.jpg'),
    getImageUrl('/images/products/PUFFER/2024-1-298266.jpg'),
    getImageUrl('/images/products/PUFFER/2024-1-298295.jpg'),
    getImageUrl('/images/products/PUFFER/55f8103c41080eb338559662a24de9f1.JPG'),
    getImageUrl('/images/products/PUFFER/94f5983b106505592a78dd44a4947769.JPG'),
    getImageUrl('/images/products/PUFFER/cf85b3dda7b080fbc760da1d24019709.JPG')
  ]
};